
<script setup lang="ts">

import { ConditionType } from "@domain/Vehicle";
import { WorkOrderType } from "@domain/CarePlan/WorkOrders/WorkOrderType";
import { FeatureType } from "@domain/CarePlan/WorkOrders/FeatureType";
import type { IMyGarageViewModel } from "@app/MyGarage";

import FeatureTile from "./Components/FeatureTile.vue";
import TabSectionHeader from "./Components/TabSectionHeader.vue";
import ProtectStatutoryWarning from "../ProtectStatutoryWarning/ProtectStatutoryWarning.vue";
import { computed } from "vue";
import dayjs from "dayjs";

const model = defineModel<IMyGarageViewModel>( { required: true } );

const tyreRepair = model.value.carePlan.carbucksProtect.workOrderConfigurations.find( i => i.type == WorkOrderType.TyreRepair && i.product == FeatureType.Protect );
const tyreReplacement = model.value.carePlan.carbucksProtect.workOrderConfigurations.find( i => i.type == WorkOrderType.TyreReplacement && i.product == FeatureType.Protect );
const battery = model.value.carePlan.carbucksProtect.workOrderConfigurations.find( i => i.type == WorkOrderType.BatteryReplacement && i.product == FeatureType.Protect );
const brakes = model.value.carePlan.carbucksProtect.workOrderConfigurations.find( i => i.type == WorkOrderType.BrakeService && i.product == FeatureType.Protect );
const wiperBlades = model.value.carePlan.carbucksProtect.workOrderConfigurations.find( i => i.type == WorkOrderType.WiperBladeReplacement && i.product == FeatureType.Protect );
const keyFob = model.value.carePlan.carbucksProtect.workOrderConfigurations.find( i => i.type == WorkOrderType.KeyFobService && i.product == FeatureType.Protect );

const hasTyreRepair = tyreRepair != null;
const hasTyreReplacement = tyreReplacement != null;
const hasBatteryReplacement = battery != null;
const hasBrakeReplacement = brakes != null;
const hasWiperBladeReplacement = wiperBlades != null;
const hasKeyFobRepair = keyFob != null;

const showStatutoryWarning = model.value.carePlan.carbucksProtect.isUsed && model.value.carePlan.carbucksProtect.dateActiveFrom && dayjs( model.value.carePlan.carbucksProtect.dateActiveFrom ).isAfter( dayjs() );

function getConditionState( type: ConditionType )
{
    const condition = model.value.vehicle.condition.getCondition( type );
    return condition?.state;
}

</script>

<template>

    <div class="bg-gray-850">

        <TabSectionHeader
            more="/my-garage/care-plan/carbucks-protect">
            <img 
                src="@/assets/images/protect.png"  
                class="h-6 3xl:h-8" 
            />
            <div class="text-white/80 text-xs 2xl:text-sm pt-2">
                Your car's wear and tear protection
            </div>
        </TabSectionHeader>

        <div class="
            grid grid-cols-3 gap-4 2xl:gap-6 px-4 2xl:px-6 pt-2
            bg-gradient-to-b from-gray-700 to-gray-850 via-50% via-gray-800
            ">
        
            <ProtectStatutoryWarning v-if="showStatutoryWarning" class="col-span-3 text-base 2xl:text-xl text-center" :is-used="model.carePlan.carbucksProtect.isUsed" :contract-start-date="model.carePlan.carbucksProtect.dateActiveFrom" />
            <h1 v-else class="col-span-3 text-base 2xl:text-xl text-center text-white/30 pb-2">
                Choose an option below and our concierge<br/>will help you organise a repair
            </h1>

            <FeatureTile 
                v-if="hasTyreRepair"
                :to="`/my-garage/care-plan/item/${WorkOrderType.TyreRepair}`"
                icon="help-me:flat-tyre" 
                :work-order-condition="tyreRepair">
                Puncture<br/>Repair
            </FeatureTile>

            <FeatureTile 
                v-if="hasTyreReplacement"
                :to="`/my-garage/care-plan/item/${WorkOrderType.TyreReplacement}/${ConditionType.Tyres}`"
                icon="dashboard:tyres" 
                :work-order-condition="tyreReplacement"
                :state="getConditionState( ConditionType.Tyres )">
                Tyre<br/>Replacement
            </FeatureTile>

            <FeatureTile 
                v-if="hasBrakeReplacement"
                :to="`/my-garage/care-plan/item/${WorkOrderType.BrakeService}`"
                icon="help-me:brakes" 
                small-icon
                :work-order-condition="brakes"
                :state="getConditionState( ConditionType.Brakes )">
                Brakes<br/>Service
            </FeatureTile>

            <FeatureTile 
                v-if="hasWiperBladeReplacement"
                :to="`/my-garage/care-plan/item/${WorkOrderType.WiperBladeReplacement}`"
                icon="help-me:wiper-blades" 
                :work-order-condition="wiperBlades"
                :state="getConditionState( ConditionType.WiperBlades )">
                Wiper Blade<br/>Replacement
            </FeatureTile>

            <FeatureTile 
                v-if="hasBatteryReplacement"
                :to="`/my-garage/care-plan/item/${WorkOrderType.BatteryReplacement}`"
                icon="dashboard:battery" 
                :work-order-condition="battery"
                :state="getConditionState( ConditionType.Battery )">
                Battery<br/>Replacement
            </FeatureTile>

            <FeatureTile 
                v-if="hasKeyFobRepair"
                :to="`/my-garage/care-plan/item/${WorkOrderType.KeyFobService}`"
                icon="help-me:keys" 
                :work-order-condition="keyFob">
                Key Fob<br/>Repair
            </FeatureTile>

        </div>

    </div>

</template>